import type { ReactNode } from 'react';
import { CdCheckbox } from '../../atoms/CdCheckbox';
import { CdStatusTag, type StatusTone } from '../../atoms/CdStatusTag';
import { CdHeading, CdText } from '../../atoms/CdTypography';
import { cx } from '../../utils';
import styles from './CdRecordCard.module.scss';

export interface CdRecordCardProps {
  id: string;
  title: string;
  status?: { label: string; tone: StatusTone };
  /** Detail lines, rendered in order (e.g. permit #, company, permit type). */
  lines: ReactNode[];
  /** Optional trailing block (e.g. address). */
  footer?: ReactNode;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  className?: string;
}

/** Mobile list row for index pages (Figma "Mobile – List view" card). */
export const CdRecordCard = ({
  id,
  title,
  status,
  lines,
  footer,
  selected = false,
  onSelectedChange,
  className,
}: CdRecordCardProps) => {
  return (
    <article
      className={cx(styles.card, selected && styles.selected, className)}
      aria-labelledby={`${id}-title`}
    >
      <header className={styles.header}>
        <CdHeading level={3} size="md" id={`${id}-title`}>
          {title}
        </CdHeading>
        <div className={styles.headerEnd}>
          {status && <CdStatusTag label={status.label} tone={status.tone} />}
          {onSelectedChange && (
            <CdCheckbox
              id={`${id}-select`}
              labelText={`Select ${title}`}
              hideLabel
              checked={selected}
              onCheckedChange={onSelectedChange}
            />
          )}
        </div>
      </header>
      <div className={styles.lines}>
        {lines.map((line, i) => (
          <CdText key={i}>{line}</CdText>
        ))}
      </div>
      {footer && (
        <CdText as="div" className={styles.footer}>
          {footer}
        </CdText>
      )}
    </article>
  );
};
